import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    useTheme,
    Divider,
    Alert
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";

const responseTypes = ["JSON", "XML"];

const sampleJSON = `{
  "records": [
    { "id": 1, "name": "Alice" },
    { "id": 2, "name": "Bob" }
  ]
}`;

const sampleXML = `<?xml version="1.0"?>
<records>
  <record><id>1</id><name>Alice</name></record>
  <record><id>2</id><name>Bob</name></record>
</records>`;

const ResponsePathEditor: React.FC = () => {
    const theme = useTheme();
    const [responseType, setResponseType] = useState("JSON");
    const [rawResponse, setRawResponse] = useState(sampleJSON);
    const [path, setPath] = useState("$.records[*]");
    const [result, setResult] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleEvaluate = () => {
        try {
            setError(null);
            if (responseType === "JSON") {
                const json = JSON.parse(rawResponse);
                // Basic JSONPath evaluator — replace with external lib like JSONPath-plus for full support
                if (path === "$.records[*]") {
                    setResult(json.records || []);
                } else {
                    setResult([]);
                    setError("Only basic path $.records[*] is supported in this prototype.");
                }
            } else if (responseType === "XML") {
                // XML parsing not implemented in this version
                setResult([]);
                setError("XML evaluation not implemented yet.");
            }
        } catch (err) {
            setError("Invalid response or path.");
        }
    };

    return (
        <Box sx={{ bgcolor: theme.palette.background.default, color: theme.palette.text.primary, minHeight: '100vh', py: 8 }}>
            <Container maxWidth="md">
                <Typography variant="h5" gutterBottom>
                    Response Path Editor
                </Typography>
                <Typography variant="body2" gutterBottom>
                    Use this tool to test extracting records from your API or XML response using JSONPath or XPath syntax.
                </Typography>

                <Paper sx={{ p: 3, mt: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Response Type</InputLabel>
                                <Select
                                    value={responseType}
                                    onChange={(e) => {
                                        const type = e.target.value;
                                        setResponseType(type);
                                        setRawResponse(type === "JSON" ? sampleJSON : sampleXML);
                                        setPath(type === "JSON" ? "$.records[*]" : "/records/record");
                                    }}
                                    label="Response Type"
                                >
                                    {responseTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                label={responseType === "JSON" ? "JSONPath" : "XPath"}
                                fullWidth
                                value={path}
                                onChange={(e) => setPath(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Raw Response"
                                fullWidth
                                multiline
                                minRows={6}
                                value={rawResponse}
                                onChange={(e) => setRawResponse(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" startIcon={<PlayArrow />} onClick={handleEvaluate}>
                                Evaluate
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1">Results</Typography>
                            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                            {!error && (
                                <Box component="pre" sx={{ mt: 2, backgroundColor: theme.palette.background.paper, p: 2, borderRadius: 2 }}>
                                    {JSON.stringify(result, null, 2)}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResponsePathEditor;